import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Typography,
  Accordion,
  AccordionDetails,
  Box,
  AccordionSummary,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import Grid2 from "@mui/material/Grid2";
import AuthContext from "../Context/AuthProvider";
import { getPrescription, viewPrescriptionById } from "../API/PrescriptionApi";
import { getServiceDetails } from "../API/LinkedServicesApi";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import { formatAge } from "../Utility/Utility";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import MenuBar from "../common/MenuBar";
import { useSnackbar } from "../Utility/UserMessageContext";

function FrozenShoulderPrescriptionReadOnly({
  serviceId,
  prescriptionId,
  uniqueTabId,
  changeTabsLabel,
  showMenuBar = true,
}) {
  const { auth } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // --- STATES ---
  const [currentService, setCurrentService] = useState();
  const [prescriptionStatus, setPrescriptionStatus] = useState("Draft");
  const [detailedPrescription, setDetailedPrescription] = useState();

  const displayText = (value, fallback = "Not available") => {
    return value !== undefined && value !== null && value !== ""
      ? value
      : fallback;
  };

  // Helper function to load service details
  const loadService = async (svcId) => {
    if (auth && svcId) {
      try {
        const result = await getServiceDetails(auth, svcId);
        if (result?.isSuccess) {
          setCurrentService(result?.data);
        } else {
          showSnackbar(
            result?.data || "Failed to load service details",
            "error",
          );
        }
      } catch (error) {
        showSnackbar(
          "Error loading service details: " + error.message,
          "error",
        );
      }
    }
  };
  let clinicalServiceCode = displayText(currentService?.serviceCode);
  let serviceName = displayText(currentService?.serviceName);

  let finalserviceName = clinicalServiceCode + " " + serviceName;

  useEffect(() => {
    const loadPrescription = async () => {
      if (auth && prescriptionId) {
        try {
          const result = await getPrescription(auth, prescriptionId);
          if (result?.isSuccess) {
            const p = result.data;
            const details = JSON.parse(p?.detailedPrescription || "{}");
            setDetailedPrescription(details);

            // Set serviceId from prescription if not provided
            if (!serviceId && p?.availedServiceId) {
              loadService(p.availedServiceId);
            }

            setPrescriptionStatus(p?.status || "Draft");
            changeTabsLabel && changeTabsLabel(uniqueTabId, `${p?.displayId}`); // Update tab label with prescription display ID
          } else {
            showSnackbar(
              result?.data || "Failed to load prescription",
              "error",
            );
          }
        } catch (error) {
          showSnackbar("Error loading prescription: " + error.message, "error");
        }
      }
    };

    // Load service first if serviceId is provided
    if (serviceId) {
      loadService(serviceId);
    }

    loadPrescription();
  }, [auth, serviceId, prescriptionId, showSnackbar]);

  const viewPrescription = async () => {
    if (auth && prescriptionId) {
      await viewPrescriptionById(auth, prescriptionId);
    }
  };

  return (
    <Grid2 container size={12}>
      {showMenuBar && (
        <Grid2 size={12}>
          <MenuBar
            onDownload={viewPrescription}
            showDownload={prescriptionStatus === "Final"}
            prescriptionStatus={prescriptionStatus}
          />
        </Grid2>
      )}

      <Grid2 container size={12}>
        <Grid2
          size={{ sx: 12, sm: 12, md: 12, lg: 9, xl: 9 }}
          sx={{ paddingTop: "10px", padding: "10px", width: "100%" }}
        >
          <Card
            className="outerCard"
            variant="outlined"
            fullWidth
            sx={{ width: "100%", padding: "10px" }}
          >
            <Grid2
              container
              spacing={1}
              size={12}
              sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
            >
              {/* Patient Information */}
              <Grid2 size={12}>
                <Grid2 container size={12}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Patient Name"
                      value={
                        currentService?.patientPrefix +
                        " " +
                        currentService?.patientFirstName +
                        " " +
                        currentService?.patientLastName
                      }
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Age and Gender"
                      value={
                        formatAge(currentService?.patientAgeInYears) +
                        " and " +
                        currentService?.patientGender
                      }
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="MRN"
                      value={displayText(currentService?.patientMRN)}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container size={12}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Visit Id"
                      value={displayText(currentService?.visitDisplayId)}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Visit Type"
                      value={displayText(currentService?.visitType)}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Visit Date Time"
                      value={convertUTCToTimeZoneDateTimeString(
                        new Date().toISOString(),
                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                      )}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 container size={12}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Service Id"
                      value={displayText(currentService?.displayId)}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Service Name"
                      value={finalserviceName}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <HeaderValueDisplay
                      header="Service Date Time"
                      value={convertUTCToTimeZoneDateTimeString(
                        new Date().toISOString(),
                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>

              {/* Chief Complaint & HOPI */}
              <Accordion
                defaultExpanded
                variant="outlined"
                sx={{ width: "100%", maxWidth: "100%" }}
              >
                <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                  <Typography component="span">
                    Chief Complaint & HOPI
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                  <Grid2
                    container
                    spacing={3}
                    size={12}
                    sx={{ justifyContent: "center", paddingX: "10px" }}
                  >
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Side
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.chiefComplaint?.complaintSide,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Duration
                      </Typography>
                      <Typography variant="body1">
                        {detailedPrescription?.chiefComplaint?.durationValue &&
                        detailedPrescription?.chiefComplaint?.durationUnit
                          ? `${detailedPrescription?.chiefComplaint?.durationValue} ${detailedPrescription?.chiefComplaint?.durationUnit}`
                          : "Not available"}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Nature of Pain
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.chiefComplaint?.natureOfPain,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Symptoms
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.chiefComplaint?.symptoms,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Onset
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.chiefComplaint?.onset,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Injury
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.chiefComplaint?.injury,
                        )}
                      </Typography>
                    </Grid2>
                    {detailedPrescription?.chiefComplaint?.injury === "Yes" && (
                      <Grid2 size={12}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          Type of Injury
                        </Typography>
                        <Typography variant="body1">
                          {displayText(
                            detailedPrescription?.chiefComplaint?.typeOfInjury,
                          )}
                        </Typography>
                      </Grid2>
                    )}
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Aggravating Factor
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.chiefComplaint
                            ?.aggravatingFactor,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Relieving Factor
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.chiefComplaint?.relievingFactor,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Night Pain
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.chiefComplaint?.nightPain,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Sleep Disturbance
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.chiefComplaint
                            ?.sleepDisturbance,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Notes
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.chiefComplaint?.notes,
                        )}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </AccordionDetails>
              </Accordion>

              {/* Past History */}
              <Accordion
                defaultExpanded
                variant="outlined"
                sx={{ width: "100%", maxWidth: "100%" }}
              >
                <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                  <Typography component="span">Past History</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                  <Grid2
                    container
                    spacing={3}
                    size={12}
                    sx={{ justifyContent: "center", paddingX: "10px" }}
                  >
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        HTN
                      </Typography>
                      <Typography variant="body1">
                        {displayText(detailedPrescription?.pastHistory?.htn)}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        DM2
                      </Typography>
                      <Typography variant="body1">
                        {displayText(detailedPrescription?.pastHistory?.dm2)}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Hypothyroidism
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.pastHistory?.hypothyroid,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Rx History
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.pastHistory?.rxHistory,
                        )}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </AccordionDetails>
              </Accordion>

              {/* On Examination */}
              <Accordion
                defaultExpanded
                variant="outlined"
                sx={{ width: "100%", maxWidth: "100%" }}
              >
                <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                  <Typography component="span">On Examination</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                  <Grid2
                    container
                    spacing={3}
                    size={12}
                    sx={{ justifyContent: "center", paddingX: "10px" }}
                  >
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Side Examined
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.examination?.examSide,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Swelling
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.examination?.swelling,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Muscle Wasting
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.examination?.muscleWasting,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Neuro Deficit
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.examination?.neuroDeficit,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Neuro Deficit Type
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.examination?.neuroDeficit,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Capsular Pattern
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.examination?.capsularPattern,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Muscle Tightness
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.examination?.muscleTightness,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Muscles Involved
                      </Typography>
                      <Typography variant="body1">
                        {detailedPrescription?.examination?.musclesInvolved
                          ?.length > 0
                          ? detailedPrescription.examination.musclesInvolved.join(
                              ", ",
                            )
                          : "None"}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Tenderness On
                      </Typography>
                      <Typography variant="body1">
                        {detailedPrescription?.examination?.tendernessOn
                          ?.length > 0
                          ? detailedPrescription.examination.tendernessOn.join(
                              ", ",
                            )
                          : "None"}
                      </Typography>
                    </Grid2>

                    {/* Range of Motion */}
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 2 }}
                      >
                        Range of Motion (ROM)
                      </Typography>
                      <Box sx={{ overflowX: "auto" }}>
                        {isMobile ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {detailedPrescription?.examination?.rom?.map(
                              (movement) => (
                                <Card
                                  variant="outlined"
                                  sx={{ p: 2 }}
                                  key={movement.name}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                    gutterBottom
                                  >
                                    {movement.displayName}
                                  </Typography>
                                  <Typography variant="body2">
                                    Normal: {movement.normal}°
                                  </Typography>
                                  <Typography variant="body2">
                                    Left:{" "}
                                    {movement.left
                                      ? `${movement.left}°`
                                      : "Not recorded"}
                                  </Typography>
                                  <Typography variant="body2">
                                    Right:{" "}
                                    {movement.right
                                      ? `${movement.right}°`
                                      : "Not recorded"}
                                  </Typography>
                                </Card>
                              ),
                            )}
                          </Box>
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                  Movement
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  Normal ROM
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  Left
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  Right
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {detailedPrescription?.examination?.rom?.map(
                                (movement) => (
                                  <TableRow key={movement.name}>
                                    <TableCell>
                                      {movement.displayName}
                                    </TableCell>
                                    <TableCell align="center">
                                      {movement.normal}°
                                    </TableCell>
                                    <TableCell align="center">
                                      {movement.left
                                        ? `${movement.left}°`
                                        : "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                      {movement.right
                                        ? `${movement.right}°`
                                        : "-"}
                                    </TableCell>
                                  </TableRow>
                                ),
                              )}
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Muscle Power
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.examination?.musclePower,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Grip Pinch
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.examination?.gripPinch,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Tone
                      </Typography>
                      <Typography variant="body1">
                        {displayText(detailedPrescription?.examination?.tone)}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Coordination
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.examination?.coordination,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Notes
                      </Typography>
                      <Typography variant="body1">
                        {displayText(detailedPrescription?.examination?.notes)}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </AccordionDetails>
              </Accordion>

              {/* Special Tests */}
              <Accordion
                defaultExpanded
                variant="outlined"
                sx={{ width: "100%", maxWidth: "100%" }}
              >
                <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                  <Typography component="span">Special Tests</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                  <Grid2
                    container
                    spacing={3}
                    size={12}
                    sx={{ justifyContent: "center", paddingX: "10px" }}
                  >
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Thumb Drop Test
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.specialTests?.thumbDropTest,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Painful Arc Test
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.specialTests?.painfulArcTest,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Notes
                      </Typography>
                      <Typography variant="body1">
                        {displayText(detailedPrescription?.specialTests?.notes)}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </AccordionDetails>
              </Accordion>

              {/* Functional Assessment */}
              <Accordion
                defaultExpanded
                variant="outlined"
                sx={{ width: "100%", maxWidth: "100%" }}
              >
                <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                  <Typography component="span">
                    Functional Assessment
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                  <Grid2
                    container
                    spacing={3}
                    size={12}
                    sx={{ justifyContent: "center", paddingX: "10px" }}
                  >
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        ADL
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.functionalAssessment?.adl,
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Difficulty In
                      </Typography>
                      <Typography variant="body1">
                        {detailedPrescription?.functionalAssessment
                          ?.difficulties?.length > 0
                          ? detailedPrescription.functionalAssessment.difficulties.join(
                              ", ",
                            )
                          : "None"}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Notes
                      </Typography>
                      <Typography variant="body1">
                        {displayText(
                          detailedPrescription?.functionalAssessment?.notes,
                        )}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </AccordionDetails>
              </Accordion>

              {/* Management Plan */}
              <Accordion
                defaultExpanded
                variant="outlined"
                sx={{ width: "100%", maxWidth: "100%" }}
              >
                <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                  <Typography component="span">Management Plan</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                  <Grid2
                    container
                    spacing={3}
                    size={12}
                    sx={{ justifyContent: "center", paddingX: "10px" }}
                  >
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Modalities
                      </Typography>
                      <Typography variant="body1">
                        {detailedPrescription?.managementPlan?.modalities
                          ?.length > 0
                          ? detailedPrescription.managementPlan.modalities.join(
                              ", ",
                            )
                          : "None"}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Exercise Plan
                      </Typography>
                      <Typography variant="body1">
                        {detailedPrescription?.managementPlan?.exercisePlan
                          ?.length > 0
                          ? detailedPrescription.managementPlan.exercisePlan.join(
                              ", ",
                            )
                          : "None"}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Prognosis
                      </Typography>
                      <Typography variant="body1">
                        {detailedPrescription?.managementPlan?.prognosis}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </AccordionDetails>
              </Accordion>
            </Grid2>
          </Card>
        </Grid2>
      </Grid2>
    </Grid2>
  );
}

export default FrozenShoulderPrescriptionReadOnly;
